import type { FC } from 'react';

import { useCms, useContent } from '@eneco/cms';
import type { CmsAdapter, CmsPage, CmsRendering } from '@eneco/cms-adapter';
import { Head } from '@eneco/next';

import type { AccordionItem } from './accordion-content.types';

type Schema = Record<string, unknown>;

/** An Accordion item; one filled in from a shared `siteWideAccordionItem` also carries that document's `_id`. */
type FaqItem = AccordionItem & { _id?: string };

interface PageContent {
  title?: string;
  image?: unknown;
  structuredData?: {
    articleType?: string;
    faq?: boolean;
    headline?: string;
  };
  structuredDataDates?: { datePublished?: string; dateModified?: string };
}

const SCHEMA_CONTEXT = 'https://schema.org';

/** Every Accordion item on the page with a question, in page order, nested slots included. */
const accordionItems = (rendering: CmsRendering | undefined): FaqItem[] => {
  if (!rendering) return [];
  const own =
    rendering.componentName === 'Accordion'
      ? ((rendering.content as { items?: FaqItem[] }).items ?? []).filter(item => item.question)
      : [];
  const nested = Object.values(rendering.placeholders ?? {}).flatMap(children => children.flatMap(accordionItems));
  return [...own, ...nested];
};

/**
 * What Sitecore's `StructuredDataSchemasContentResolver` outputs, from the page itself (ADR-0104): an
 * FAQPage from the page's Accordions, and the article for a page whose settings name an article type.
 */
const buildSchemas = (page: CmsPage, adapter: CmsAdapter): Schema[] => {
  const content = (page.root.content ?? {}) as PageContent;
  const settings = content.structuredData ?? {};
  const schemas: Schema[] = [];

  // An item two accordions share is one question, as on Sitecore. A shared document is told by its `_id`, as
  // the Studio gives each reference to it a `_key` of its own. An inline item is told by its `_key`, which the
  // migration sets to the Sitecore item, and its question: a copied accordion keeps its items' keys, and an
  // edited copy is a question of its own.
  const seen = new Set<string>();
  const items = (settings.faq === false ? [] : accordionItems(page.root)).filter(item => {
    const key = item.id ?? item._id ?? `${item._key ?? ''}\u0000${item.question}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  if (items.length > 0) {
    schemas.push({
      '@context': SCHEMA_CONTEXT,
      '@type': 'FAQPage',
      mainEntity: items.map(item => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: String(adapter.toRichText(item.answer) ?? '') },
      })),
    });
  }

  if (settings.articleType && settings.articleType !== 'none') {
    schemas.push({
      '@context': SCHEMA_CONTEXT,
      '@type': settings.articleType,
      headline: settings.headline || content.title,
      image: adapter.toImage(content.image)?.src,
      datePublished: content.structuredDataDates?.datePublished,
      dateModified: content.structuredDataDates?.dateModified,
    });
  }

  return schemas;
};

/**
 * `StructuredDataSchema` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors `libs/sitecore/components/system/src/StructuredDataSchema/StructuredDataSchema.tsx`: the page's
 * JSON-LD in one `<script>` in the head. Sitecore computes the schemas on its server and hands them over
 * in the rendering's fields, which reach `content` unchanged. The Sanity block has no fields: on a
 * page from Sanity the schemas are built from the page document and its Accordions, as ADR-0104 decides,
 * with the dates the page query works out (`libs/cms-sanity/src/queries.ts#STRUCTURED_DATA_DATES_PROJECTION`).
 *
 * Known simplifications: the Organization, WebSite and BreadcrumbList results ADR-0104 adds, and the
 * article's author from `structuredData.authorName`, are left out, as Sitecore outputs none of them today.
 * The questions come in page order; Sitecore's resolver walks the accordions in its own order, and also
 * reads accordions the page does not show (personalised ones), which the Sanity page does not hold.
 */
const StructuredDataSchema: FC = () => {
  const { page, adapter } = useCms();
  const content = useContent<{ schemas?: Schema[] }>();
  const schemas = page.context.cmsSource === 'sanity' ? buildSchemas(page, adapter) : content.schemas;

  if (!schemas?.length) return null;

  return (
    <Head>
      <script type="application/ld+json">{JSON.stringify(schemas)}</script>
    </Head>
  );
};

export default StructuredDataSchema;
